import i18n from '../../../i18n';
import type { OpsMode } from '../hooks/useOpsCommand';
import type { PerfSettings } from '../types';
import {
  loadAgentPrefs,
  type AgentPrefs,
} from './agentPrefs';

export type UserSituationProfile = {
  mode: OpsMode;
  scopeShape: 'single' | 'fleet';
  outletCount: number;
  hasNorthStar: boolean;
  /** Soft density hint for brief + chips. */
  density: 'compact' | 'standard' | 'rich';
  /** Suggested dig-in chips for the chat lane — short questions only. */
  chips: string[];
  prefs: AgentPrefs;
};

const CHIP_MAX = 48;

function hourBucket(now = new Date()): 'pre' | 'service' | 'post' | 'review' {
  const h = now.getHours();
  if (h < 11) return 'pre';
  if (h < 16) return 'service';
  if (h < 23) return 'service';
  return 'post';
}

/** Turn polluted careAbout / ask prompts into a short chip label, or drop. */
export function normalizeCareChip(raw: string): string | null {
  let t = raw.trim().replace(/\s+/g, ' ');
  const tellMore = t.match(/^Tell me more about:\s*(.+?)(?:\.\s*Why\b|$)/i);
  if (tellMore) t = tellMore[1].trim();
  if (/^Tell me more\b/i.test(t)) return null;
  if (isScopeBoundInsightChip(t)) return null;
  if (t.length < 4 || t.length > CHIP_MAX) return null;
  return t;
}

/** Catalog copy is period/scope-specific — never a global conversation starter. */
export function isScopeBoundInsightChip(text: string): boolean {
  return (
    /\bvs (their |this house|the previous)/i.test(text) ||
    /over the last \d+ matching/i.test(text) ||
    /\bclimbed from\b/i.test(text)
  );
}

export function buildUserSituationProfile(args: {
  mode: OpsMode;
  outletCount: number;
  hasNorthStar: boolean;
  perfSettings?: PerfSettings;
}): UserSituationProfile {
  const prefs = loadAgentPrefs();
  const outletCount = Math.max(0, args.outletCount);
  const scopeShape = outletCount <= 1 ? 'single' : 'fleet';
  const bucket = hourBucket();

  let density: UserSituationProfile['density'] = 'standard';
  if (scopeShape === 'fleet') density = 'rich';
  if (args.mode === 'live' && bucket === 'service') density = 'compact';
  if (prefs.careAbout.length >= 3) density = 'rich';

  const chips = chipsFor(args.mode, scopeShape, args.hasNorthStar, prefs);

  return {
    mode: args.mode,
    scopeShape,
    outletCount,
    hasNorthStar: args.hasNorthStar,
    density,
    chips,
    prefs,
  };
}

function chipsFor(
  mode: OpsMode,
  scopeShape: 'single' | 'fleet',
  hasNorthStar: boolean,
  prefs: AgentPrefs,
): string[] {
  // At most one personal chip — never dump raw ask prompts.
  const personal = prefs.careAbout
    .map(normalizeCareChip)
    .filter((c): c is string => Boolean(c))
    .slice(0, 1);

  const curated =
    mode === 'performance'
      ? [
          hasNorthStar
            ? i18n.t('chrome:chips.northStarTracking')
            : i18n.t('chrome:chips.northStarSuggest'),
          scopeShape === 'fleet'
            ? i18n.t('chrome:chips.outletsDragging')
            : i18n.t('chrome:chips.whatMoved'),
          i18n.t('chrome:chips.noshowWorst'),
          i18n.t('chrome:chips.oneAction'),
        ]
      : mode === 'live'
        ? [
            i18n.t('chrome:chips.attentionNow'),
            i18n.t('chrome:chips.recoverTonight'),
            i18n.t('chrome:chips.vipRequests'),
          ]
        : mode === 'growth'
          ? [
              i18n.t('chrome:chips.growthWhatChanged'),
              i18n.t('chrome:chips.growthChannelsReturn'),
              i18n.t('chrome:chips.growthTagging'),
              i18n.t('chrome:chips.growthWorthPushing'),
            ]
          : [
            i18n.t('chrome:chips.settingsRisks'),
            i18n.t('chrome:chips.staleProvisionals'),
            i18n.t('chrome:chips.tightenReliability'),
          ];

  const seen = new Set<string>();
  const out: string[] = [];
  for (const chip of [...personal, ...curated]) {
    const key = chip.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(chip);
    if (out.length >= 4) break;
  }
  return out;
}
